import React, { useState, useMemo, useCallback, useContext } from "react";
import { connectRefinementList } from "react-instantsearch-dom";
import classNames from "classnames";
import { ChevronDown, ChevronRight } from "lucide-react";
import { buildTKSkillHierarchy } from "utils/common";
import { TK_SKILL_CATEGORY_MAP } from "lookup";
import { orderBy } from "lodash";
import { SearchContext } from "context/providers";
import usePersistentFacetSearch from "./usePersistentFacetSearch";

const HIERARCHY_DELIM = " > ";

const EXP_ORDER = { low: 0, medium: 1, high: 2 };

function localHighlight(text, query, fullWords = true) {
  if (!query?.trim() || !text) {
    return text || "";
  }

  // Match words inside quotes or single words outside of quotes
  let words = query.match(/"[^"]+"|[^,\s|;:/\\\-&_+]+/g) || []; // Match quoted phrases or single words

  words = words
    .map((word) => word.replace(/"/g, "")) // Remove double quotes
    .filter((word) => word.trim() !== ",") // Exclude the ',' symbol
    .map((word) => word.replace(/[.*#+?^${}()|[\]\\]/g, "\\$&"))
    .filter((e) => e); // Escape special regex characters

  // case insensitive
  const fullWordsType = fullWords
    ? `(\\b(${words.join("|")})\\b)`
    : `(${words.join("|")})`;
  const regex = new RegExp(fullWordsType, "gi");

  // Replace matched terms with highlighted versions using <mark>
  return text.replace(
    regex,
    "<ais-highlight-0000000000>$1</ais-highlight-0000000000>"
  );
}

const convertToArray = (node, customCurrentRefinement) => {
  const arr = Object.values(node).map((child) => ({
    ...child,
    children: convertToArray(child.children, customCurrentRefinement),
  }));

  return orderBy(
    arr,
    [
      (item) =>
        item.children.length > 0 &&
        (item.isRefined ||
          customCurrentRefinement.some(
            (r) =>
              item.value &&
              (r === item.value ||
                r.startsWith(`${item.value}${HIERARCHY_DELIM}`))
          ))
          ? 0
          : 1,

      (item) =>
        EXP_ORDER[item.label.toLowerCase()] !== null
          ? -EXP_ORDER[item.label.toLowerCase()]
          : Infinity,
      (item) => -(item.count || 0),
      (item) => item.label.toLowerCase(),
    ],
    ["asc", "asc", "asc", "asc"]
  );
};

function _getMissingExpItems(items) {
  const existing = new Set(items.map(({ label }) => label));
  const levels = ["low", "medium", "high"];
  const missing = [];
  const seenBase = new Set();

  for (const { label } of items) {
    const [category, name] = label.split(HIERARCHY_DELIM);
    if (!name) continue;

    const baseLVL = [category, name].join(HIERARCHY_DELIM);
    if (seenBase.has(baseLVL)) continue;
    seenBase.add(baseLVL);

    for (const level of levels) {
      const newLabel = [baseLVL, level].join(HIERARCHY_DELIM);
      if (!existing.has(newLabel)) {
        missing.push({
          label: newLabel,
          value: [newLabel],
          count: null,
          isRefined: false,
        });
      }
    }
  }

  return missing;
}

const buildHierarchyTree = (items, TKSkillsHierarchyExp) => {
  const tree = {};

  const newItemsMissingExp = TKSkillsHierarchyExp
    ? _getMissingExpItems(items)
    : [];

  [...items, ...newItemsMissingExp].forEach((item) => {
    const parts = item.label.split(HIERARCHY_DELIM);
    let currentNode = tree;
    let currentPath = "";

    parts.forEach((part, index) => {
      currentPath = index === 0 ? part : `${currentPath} > ${part}`;

      if (!currentNode[part]) {
        currentNode[part] = {
          value: currentPath,
          label: part,
          count: 0,
          isRefined: false,
          children: {},
          originalItem: null,
        };
      }

      if (currentPath === item.label) {
        currentNode[part].count = item.count;
        currentNode[part].isRefined = item.isRefined;
        currentNode[part].originalItem = item;

        // custom fields
        currentNode[part].staticSkillsCount = item.staticSkillsCount;
      }

      currentNode = currentNode[part].children;
    });
  });

  return tree;
};

const transformLabelTerm = (value, labelSkillsStr) => {
  switch (value) {
    case "low":
      return `0-2y`;
    case "medium":
      return `2-5y`;
    case "high":
      return `5+y`;
    case "CERTIFICATION":
      return "Certification";
    case "IT":
      return `IT (Tech)${labelSkillsStr ? ` ${labelSkillsStr}` : ""}`;
    case "LANGUAGE":
      return `Language${labelSkillsStr ? ` ${labelSkillsStr}` : ""}`;
    case "PROFESSIONAL":
      return `Professional${labelSkillsStr ? ` ${labelSkillsStr}` : ""}`;
    case "SOFT":
      return `Soft ${labelSkillsStr ? ` ${labelSkillsStr}` : ""}`;
    default:
      return value;
  }
};

const TreeNode = React.memo(
  ({
    parent,
    node,
    refine,
    customCurrentRefinement,
    limit,
    showMoreLimit,
    isFromSearch,
    inputSearchRefValue,
    searchState,
    setSearchState,
    labelSkillsStr,
    attribute,
    TKSkillsHierarchyExp,
    TKSkillsHierarchyExpAttrName,
  }) => {
    const [isOpen, setIsOpen] = useState(true);

    const [showMore, setShowMore] = useState(false);

    const handleCheckboxChange = useCallback(
      (node, checked) => {
        const newItem = node.value;

        // Remove any selected descendants of this node
        let newRefinement = customCurrentRefinement.filter((item) => {
          const isDescendant = item.startsWith(`${newItem}${HIERARCHY_DELIM}`);
          const isAncestor = newItem.startsWith(`${item}${HIERARCHY_DELIM}`);
          return !isDescendant && !isAncestor;
        });

        if (checked) {
          // just add this leaf
          if (!newRefinement.includes(newItem)) {
            newRefinement.push(newItem);
          }
        } else {
          // remove the leaf itself
          newRefinement = newRefinement.filter((item) => item !== newItem);

          // add only the immediate parent
          const parts = newItem.split(HIERARCHY_DELIM);

          parts.pop();

          if (parts.length > 1) {
            const parentPath = parts.join(HIERARCHY_DELIM);
            if (!newRefinement.includes(parentPath)) {
              newRefinement.push(parentPath);
            }
          }
        }

        if (TKSkillsHierarchyExp) {
          const expRefinements = newRefinement.filter(
            (e) =>
              e.includes("low") || e.includes("medium") || e.includes("high")
          );

          const noExpRefinements = newRefinement.filter(
            (e) =>
              !(e.includes("low") || e.includes("medium") || e.includes("high"))
          );

          setSearchState({
            ...searchState,
            refinementList: {
              ...searchState.refinementList,
              [attribute]: noExpRefinements,
            },
            [TKSkillsHierarchyExpAttrName]: expRefinements,
          });
        } else {
          refine(newRefinement);
        }
      },

      [
        attribute,
        customCurrentRefinement,
        refine,
        searchState,
        setSearchState,
        TKSkillsHierarchyExp,
        TKSkillsHierarchyExpAttrName,
      ]
    );

    const hasDescendantRefined = node.value
      ? customCurrentRefinement.some(
          (r) =>
            r === node.value || r.startsWith(`${node.value}${HIERARCHY_DELIM}`)
        )
      : false;

    const isRefined = node.isRefined || hasDescendantRefined;

    const childrenAreExp = node?.children?.some(
      (e) => e.label === "low" || e.label === "medium" || e.label === "high"
    );

    const hasChildren = node.children && node.children.length > 0;

    const finalItems = !childrenAreExp
      ? node.children.slice(0, !showMore ? limit : showMoreLimit)
      : [...node.children];

    const labelStringName = transformLabelTerm(node.label, labelSkillsStr);

    let count = "";

    if (node.staticSkillsCount === undefined) {
      count = node.count || ""; // || (!parent ? `${node.children.length} length` : "");
    } else if (
      node.children?.length === 0 ||
      node.children.some((e) => e.staticSkillsCount)
    ) {
      count = node.staticSkillsCount || "";
    }

    return (
      <div>
        <div
          className={classNames({
            "flex items-center gap-x-0.5 mt-2": !parent,
          })}
        >
          {hasChildren && !parent && (
            <button
              onClick={() => setIsOpen((prev) => !prev)}
              className="cursor-pointer w-5"
            >
              {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
          )}
          <li
            key={node.label}
            className={classNames("mt-1 ais-RefinementList-item", {
              "ais-RefinementList-item--selected": isRefined,
            })}
          >
            <label
              className={classNames(
                "flex items-center text-sm whitespace-nowrap ais-RefinementList-label",
                { "gap-x-2": isFromSearch }
              )}
            >
              {parent && (
                <input
                  type="checkbox"
                  className="ais-RefinementList-checkbox mr-0"
                  checked={isRefined}
                  onChange={({ target }) => {
                    handleCheckboxChange(node, target.checked);
                  }}
                />
              )}
              {isFromSearch ? (
                <span
                  className="prose"
                  dangerouslySetInnerHTML={{
                    __html: localHighlight(
                      labelStringName,
                      inputSearchRefValue,
                      false
                    ),
                  }}
                />
              ) : (
                <span
                  title={labelStringName}
                  className={classNames(
                    "mx-2 overflow-hidden text-ellipsis capitalize",
                    {
                      "font-bold": isRefined,
                    },
                    {
                      "text-xs":
                        node.label === "low" ||
                        node.label === "medium" ||
                        node.label === "high",
                    },
                    {
                      "!mr-2 !mx-0": !parent,
                    }
                  )}
                >
                  {labelStringName}
                </span>
              )}

              <span className="px-1 border rounded-full bg-gray-100">
                {count}
              </span>
            </label>
          </li>
        </div>
        {isOpen && hasChildren && (
          <>
            <div
              className={classNames("flex flex-col pl-5", {
                "!pl-1.5": childrenAreExp,
              })}
            >
              <div
                className={classNames(
                  "mt-1.5",
                  {
                    "flex gap-2 flex-wrap": childrenAreExp,
                  },
                  {
                    "flex flex-col gap-y-4 ": !childrenAreExp,
                  }
                )}
              >
                {finalItems.map((child) => (
                  <TreeNode
                    parent={node}
                    key={child.value}
                    node={child}
                    refine={refine}
                    customCurrentRefinement={customCurrentRefinement}
                    limit={limit}
                    showMoreLimit={showMoreLimit}
                    isFromSearch={isFromSearch}
                    inputSearchRefValue={inputSearchRefValue}
                    searchState={searchState}
                    setSearchState={setSearchState}
                    labelSkillsStr={labelSkillsStr}
                    attribute={attribute}
                    TKSkillsHierarchyExp={TKSkillsHierarchyExp}
                    TKSkillsHierarchyExpAttrName={TKSkillsHierarchyExpAttrName}
                  />
                ))}
              </div>
            </div>

            {!parent && node.children.length > limit && (
              <button
                onClick={() => setShowMore((prev) => !prev)}
                className="ais-RefinementList-showMore w-fit ml-4 mb-2"
              >
                {!showMore ? "Show More" : "Show Less"}
              </button>
            )}
          </>
        )}
      </div>
    );
  }
);

const HierarchicalSkillFilterComponent = ({
  items,
  refine,
  customCurrentRefinement,
  limit,
  showMoreLimit,
  inputSearchRefValue,
  isFromSearch,
  searchState,
  setSearchState,
  labelSkillsStr,
  attribute,
  TKSkillsHierarchyExp,
  TKSkillsHierarchyExpAttrName,
}) => {
  const hierarchicalItems = useMemo(() => {
    const tree = buildHierarchyTree(items, TKSkillsHierarchyExp);

    const treeArray = convertToArray(tree, customCurrentRefinement);

    return treeArray;
  }, [customCurrentRefinement, items, TKSkillsHierarchyExp]);

  return (
    <div>
      <ul className="ais-RefinementList-list my-3">
        {hierarchicalItems.map((node) => (
          <TreeNode
            key={node.value}
            node={node}
            refine={refine}
            customCurrentRefinement={customCurrentRefinement}
            limit={limit}
            showMoreLimit={showMoreLimit}
            isFromSearch={isFromSearch}
            inputSearchRefValue={inputSearchRefValue}
            searchState={searchState}
            setSearchState={setSearchState}
            labelSkillsStr={labelSkillsStr}
            attribute={attribute}
            TKSkillsHierarchyExp={TKSkillsHierarchyExp}
            TKSkillsHierarchyExpAttrName={TKSkillsHierarchyExpAttrName}
          />
        ))}
      </ul>
    </div>
  );
};

const StaticSkills = ({ title, skills, ...rest }) => {
  return (
    <div className="mb-4">
      {title && (
        <p className="tracking-wider text-sm font-semibold mt-2">{title}</p>
      )}
      <HierarchicalSkillFilterComponent {...rest} items={skills} />
    </div>
  );
};

const getFormattedSkills = (
  TKSkills,
  currentItems,
  customCurrentRefinement
) => {
  const jobOppSkillsHierarchy = buildTKSkillHierarchy(TKSkills || []);

  const finalSkills = jobOppSkillsHierarchy.map((e) => ({
    label: e,
    value: [e],
    count: null,
    staticSkillsCount: 0,
  }));

  for (const newItem of finalSkills) {
    newItem.isRefined = !!customCurrentRefinement?.find(
      (cr) => newItem.label === cr
    );

    const foundItem = currentItems?.find((cr) => newItem.label === cr.label);

    if (foundItem) {
      newItem.staticSkillsCount = foundItem.count;
    }
  }

  return finalSkills;
};

const HierarchicalSkillFilter = ({
  searchable,
  placeholder = "Search",
  searchForItems,
  TKSkills,
  optionalTKSkills,
  TKSkillsHierarchyExp,
  ...rest
}) => {
  const { searchState, setSearchState } = useContext(SearchContext);
  const {
    query,
    updateQuery,
    visibleItems,
    isSearching: isSearchingFacet,
  } = usePersistentFacetSearch({
    items: rest.items,
    isFromSearch: rest.isFromSearch,
    searchForItems,
  });

  const customCurrentRefinement = [
    ...(rest.currentRefinement || []),
    ...(TKSkillsHierarchyExp || []),
  ];

  const requiredSkills = getFormattedSkills(
    TKSkills,
    visibleItems,
    customCurrentRefinement
  );

  const optionalSkills = getFormattedSkills(
    optionalTKSkills,
    visibleItems,
    customCurrentRefinement
  );

  const finalItems = visibleItems.filter(
    ({ label }) =>
      !requiredSkills.some(
        (rsk) => rsk.label === label && !TK_SKILL_CATEGORY_MAP[label]
      ) &&
      !optionalSkills.some(
        (rsk) => rsk.label === label && !TK_SKILL_CATEGORY_MAP[label]
      )
  );

  const searchSkill = (skills) => {
    return skills?.filter(({ label }) => {
      const normalizedItem = label.toLowerCase();
      const normalizedSearchTerm = query.toLowerCase();

      // Escaping special characters in the search term for the regular expression
      const escapedSearchTerm = normalizedSearchTerm.replace(
        /[.*+?^${}()|[\]\\]/g,
        "\\$&"
      );

      // Creating a regular expression for a case-insensitive search
      const regex = new RegExp(escapedSearchTerm, "gi");

      return regex.test(normalizedItem);
    });
  };

  const filteredReqSkills = searchSkill(requiredSkills);

  const filteredOptSkills = searchSkill(optionalSkills);

  return (
    <>
      {searchable && (
        <div className="ais-SearchBox w-full mb-2">
          <div className="ais-SearchBox-form">
            <input
              className="ais-SearchBox-input"
              type="search"
              placeholder={placeholder}
              value={query}
              onChange={(event) => updateQuery(event.currentTarget.value)}
            />
          </div>
        </div>
      )}

      {filteredReqSkills.length > 0 && (
        <StaticSkills
          title="Required Skills:"
          skills={filteredReqSkills}
          {...rest}
          isFromSearch={isSearchingFacet}
          customCurrentRefinement={customCurrentRefinement}
          searchState={searchState}
          setSearchState={setSearchState}
          TKSkillsHierarchyExp={TKSkillsHierarchyExp}
          inputSearchRefValue={query}
          labelSkillsStr=" "
        />
      )}

      {filteredOptSkills.length > 0 && (
        <StaticSkills
          title="Optional Skills:"
          skills={filteredOptSkills}
          {...rest}
          isFromSearch={isSearchingFacet}
          customCurrentRefinement={customCurrentRefinement}
          searchState={searchState}
          setSearchState={setSearchState}
          TKSkillsHierarchyExp={TKSkillsHierarchyExp}
          inputSearchRefValue={query}
          labelSkillsStr=" "
        />
      )}

      {(TKSkills?.length > 0 || optionalTKSkills?.length > 0) && (
        <div className="border-b-2 w-full my-2 mb-4" />
      )}

      <HierarchicalSkillFilterComponent
        {...rest}
        isFromSearch={isSearchingFacet}
        customCurrentRefinement={customCurrentRefinement}
        searchState={searchState}
        setSearchState={setSearchState}
        TKSkillsHierarchyExp={TKSkillsHierarchyExp}
        items={finalItems}
        inputSearchRefValue={query}
        labelSkillsStr="Skills"
      />
    </>
  );
};

export default connectRefinementList(HierarchicalSkillFilter);
